import { Injectable, OnDestroy } from '@angular/core';
import { Structure } from '@docma-mm-core/structure/structure';
import { CoEventService, StructureEventData, StructureReorderEventData } from '@mydocma/co';
import { Task, TaskScopeEnum, TaskService, TaskStatusEnum } from '@mydocma/task';
import { merge, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { StructureService } from '../../mm-common/structure/structure.service';
import { MmProjectLoaderService } from '../../mm-project/mm-project-loader.service';
import { MmFilterService } from '../mm-filter.service';
import { JwtAuthService, ClaimTypes } from '@mydocma/auth';
import { SchemaApiService } from '../../mydocma-common/schema/schema-api.service';
import { ProjectService } from '@mydocma/project';

export interface StructureSyncPatch {
  op: 'update' | 'delete' | 'reorder' | 'create';
  node: Structure;
  parent?: Structure;
}

export enum StructureSyncErrorKind {
  BackendFailed = 'backend-failed',
  ResolutionFailed = 'resolution-failed',
}

export interface StructureSyncError {
  kind: StructureSyncErrorKind;
  reason?: string;
  info: Record<string, unknown>;
}

interface StructureSyncPayload {
  op: string;
  projectId?: string;
  mydocmaUid?: string;
  name?: string;
  desc?: string;
  parentId?: string;
  newSortId?: number;
  level?: number;
  sortId?: number;
}

interface StructureSyncTaskPayload {
  schemaId?: string;
  projectStructureId?: string;
  structureId?: number | string;
  schemaProjectStructureId?: string;
  externalId?: number | string;
}

interface StructureSyncTaskContext {
  task: Task;
  projectStructureId: string | undefined;
  resolvedStructureId: number | null;
  payload: StructureSyncTaskPayload;
}

interface ShellData {
  uid: string;
  parentUid?: string;
  name?: string;
  desc?: string;
  level?: number;
  sortId?: number;
}

type CoEventOp = 'update' | 'delete' | 'reorder' | 'create';

@Injectable()
export class MmStructureSyncService implements OnDestroy {

  private static readonly STRUCTURE_SYNC_TASK_NAME_PATTERN = /^Export for Project Structure ([0-9a-f-]+) for schema ([0-9a-f-]+)$/i;
  private static readonly LOCAL_APPLY_TTL_MS = 10000;

  private readonly recentlyAppliedUids = new Map<string, number>();
  private readonly pendingShells = new Map<string, ShellData>();
  private readonly schemasByCustomer = new Map<string, any[]>();
  private currentSchemaUuid: string | null = null;

  private readonly patchSubject = new Subject<StructureSyncPatch>();
  public readonly patch$: Observable<StructureSyncPatch> = this.patchSubject.asObservable();

  private readonly errorsSubject = new Subject<StructureSyncError>();
  public readonly errors$: Observable<StructureSyncError> = this.errorsSubject.asObservable();

  private readonly subscription = new Subscription();

  constructor(
    private readonly taskService: TaskService,
    private readonly structureService: StructureService,
    private readonly loaderService: MmProjectLoaderService,
    private readonly coEventService: CoEventService,
    private readonly filterService: MmFilterService,
    private readonly jwtAuthService: JwtAuthService,
    private readonly schemaApiService: SchemaApiService,
    private readonly projectService: ProjectService,
  ) {
    this.taskService.observeTasks([TaskScopeEnum.user, TaskScopeEnum.client, TaskScopeEnum.customer]);
    this.subscription.add(
      this.projectService.currentProject.pipe(
        filter(Boolean),
        map(project => (project as any)?.id),
        distinctUntilChanged(),
        tap(() => this.currentSchemaUuid = null),
        switchMap(() => this.resolveCurrentSchema$()),
      ).subscribe(uuid => this.currentSchemaUuid = uuid)
    );

    this.subscription.add(this.taskStream().pipe(filter(t => this.isMatchingStructureSyncTask(t) && this.belongsToCurrentSchema(t))).subscribe(t => this.handleStructureSyncTask(t)));
    this.subscription.add(this.taskStream().pipe(filter(t => this.isFailedStructureSyncTask(t) && this.belongsToCurrentSchema(t))).subscribe(t => this.handleFailedStructureSyncTask(t)));

    const coBindings: Array<[CoEventOp, Observable<StructureEventData | StructureReorderEventData>]> = [
      ['update', this.coEventService.onStructureUpdated],
      ['delete', this.coEventService.onStructureDeleted],
      ['reorder', this.coEventService.onStructureReordered as Observable<StructureEventData | StructureReorderEventData>],
      ['create', this.coEventService.onStructureCreated],
    ];
    coBindings.forEach(([op, obs]) => this.subscription.add(obs.subscribe(event => this.handleCoEvent(op, event))));
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private taskStream(): Observable<Task> {
    return merge(this.taskService.onTaskStarted, this.taskService.onTaskChanged);
  }

  private handleCoEvent(op: CoEventOp, event: StructureEventData | StructureReorderEventData): void {
    if (!event?.node?.id) return;
    const payload: StructureSyncPayload = {
      op,
      projectId: (event.tree as any)?.projectId ?? event.node?.projectId,
      mydocmaUid: event.node.id,
      name: event.node.name,
      desc: event.node.desc,
      parentId: op === 'reorder' ? (event as StructureReorderEventData).parentId : event.node.parentId,
      newSortId: op === 'reorder' ? (event as StructureReorderEventData).newSortId : undefined,
      level: event.node.level,
    };
    if (!this.belongsToCurrentProject(payload.projectId)) return;
    this.applyPayload(payload);
  }

  private isMatchingStructureSyncTask(task: Task): boolean {
    return !!task?.name
      && MmStructureSyncService.STRUCTURE_SYNC_TASK_NAME_PATTERN.test(task.name)
      && task?.status?.value === TaskStatusEnum.ended;
  }

  private isFailedStructureSyncTask(task: Task): boolean {
    return !!task?.name
      && MmStructureSyncService.STRUCTURE_SYNC_TASK_NAME_PATTERN.test(task.name)
      && task?.status?.value === TaskStatusEnum.failed;
  }

  private handleFailedStructureSyncTask(task: Task): void {
    const failureMessage = this.getStructureSyncTaskFailureMessage(task);
    const match = task?.name?.match(MmStructureSyncService.STRUCTURE_SYNC_TASK_NAME_PATTERN);
    const uidFromName = match?.[1];
    const droppedShell = uidFromName ? this.pendingShells.delete(uidFromName) : false;
    const info = {
      name: task?.name,
      statusValue: task?.status?.value,
      statusDataType: task?.status?.data?.type,
      statusText: task?.status?.data?.text,
      taskId: (task as any)?.id,
      failureMessage,
      droppedShell,
    };
    this.errorsSubject.next({ kind: StructureSyncErrorKind.BackendFailed, reason: failureMessage ?? undefined, info });
  }

  private handleStructureSyncTask(task: Task): void {
    try {
      const ctx = this.buildStructureSyncTaskContext(task);
      if (!ctx) return;

      const data = this.loaderService.getCurrentStructures();
      if (!data?.length) return;

      if (!ctx.projectStructureId) {
        this.notifyStructureSyncResolutionFailure(
          'Structure sync task payload missing projectStructureId.',
          { taskName: task.name, payload: ctx.payload },
        );
        return;
      }

      if (!ctx.resolvedStructureId) {
        this.notifyStructureSyncResolutionFailure(
          'No usable numeric structure id was provided by the structure sync task.',
          { uid: ctx.projectStructureId, structureId: ctx.payload.structureId, externalId: ctx.payload.externalId },
        );
        return;
      }

      const existing = this.findByMyDocmaUid(data, ctx.projectStructureId);
      if (existing) {
        this.pendingShells.delete(ctx.projectStructureId);
        this.fetchAndApplyNodeUpdate(existing, ctx);
        return;
      }

      const shell = this.pendingShells.get(ctx.projectStructureId);
      if (!shell) return;
      this.resolveShellToTree(shell, ctx, data);
    } catch (e) {
      console.error('Structure sync task parse failed', e);
    }
  }

  private resolveCurrentSchema$(): Observable<string | null> {
    const claims = this.jwtAuthService.getAccessTokenClaims() ?? {};
    const schemaName = claims[ClaimTypes.db_schema];
    const customerId = claims[ClaimTypes.customer_id];
    if (!schemaName || !customerId) return of(null);
    const cached = this.schemasByCustomer.get(customerId);
    const list$ = cached
      ? of(cached)
      : this.schemaApiService.getAll(customerId).pipe(tap(schemas => this.schemasByCustomer.set(customerId, schemas ?? [])));
    return list$.pipe(
      map(schemas => (schemas ?? []).find(s => s?.name === schemaName)?.id ?? null),
      catchError(() => of(null)),
    );
  }

  private belongsToCurrentSchema(task: Task): boolean {
    if (!this.currentSchemaUuid) return true;
    const taskSchemaUuid = task?.name?.match(MmStructureSyncService.STRUCTURE_SYNC_TASK_NAME_PATTERN)?.[2];
    if (!taskSchemaUuid) return true;
    return taskSchemaUuid === this.currentSchemaUuid;
  }

  private buildStructureSyncTaskContext(task: Task): StructureSyncTaskContext | null {
    const extracted = this.extractStructureSyncTaskPayload(task);
    if (!extracted.payload) {
      this.notifyStructureSyncResolutionFailure(
        'Structure sync task payload was missing or had an unsupported shape.',
        { payloadSource: extracted.source, rawData: extracted.rawData, taskName: task.name },
      );
      return null;
    }

    const payload = extracted.payload;
    return {
      task,
      projectStructureId: payload.projectStructureId,
      resolvedStructureId: this.toPositiveNumber(payload.structureId ?? payload.externalId),
      payload,
    };
  }

  private resolveShellToTree(shell: ShellData, ctx: StructureSyncTaskContext, data: Structure[]): void {
    const parent = (shell.parentUid ? this.findByMyDocmaUid(data, shell.parentUid) : null) ?? data[0];
    if (!parent) {
      this.pendingShells.delete(shell.uid);
      return;
    }

    const node = new Structure({
      Id: ctx.resolvedStructureId!,
      MyDocmaUid: shell.uid,
      Name1: shell.name ?? '',
      Name2: shell.desc ?? '',
      Level: shell.level ?? ((parent.Level ?? 0) + 1),
      Children: [],
      Parent: { Id: parent.Id } as any,
      RootId: parent.RootId,
      selectable: true,
    } as Partial<Structure>);

    const inserted = this.coreInsertNode(data, node, parent.MyDocmaUid, shell.sortId);
    if (!inserted) {
      this.pendingShells.delete(shell.uid);
      return;
    }

    if (this.patchSubject.observed) {
      this.patchSubject.next({ op: 'create', node: inserted.node, parent: inserted.parent });
    } else {
      this.loaderService.notifyStructuresChanged();
    }
    this.maybePersist();
    this.pendingShells.delete(shell.uid);
  }

  private fetchAndApplyNodeUpdate(existing: Structure, ctx: StructureSyncTaskContext): void {
    const structureId = ctx.resolvedStructureId!;
    if (this.wasRecentlyApplied(existing.MyDocmaUid)) return;
    this.subscription.add(
      this.structureService.getStructures(structureId, 1, 0, 1).subscribe({
        next: response => this.applyFetchedNodeUpdate(existing, response?.Data?.[0] ?? null, ctx),
        error: e => this.notifyStructureSyncResolutionFailure(
          'Failed to fetch the updated structure from backend.',
          { uid: ctx.projectStructureId, structureId, error: this.serializeError(e) },
        ),
      })
    );
  }

  private applyFetchedNodeUpdate(existing: Structure, fetched: Structure | null, ctx: StructureSyncTaskContext): void {
    if (!fetched) {
      this.notifyStructureSyncResolutionFailure(
        'Fetched node response was empty.',
        { uid: ctx.projectStructureId, structureId: ctx.resolvedStructureId },
      );
      return;
    }
    const data = this.loaderService.getCurrentStructures();
    if (!data?.length) return;

    const target = this.coreUpdate(data, existing.MyDocmaUid, fetched.Name1, fetched.Name2);
    if (!target) return;

    if (this.patchSubject.observed) {
      this.patchSubject.next({ op: 'update', node: target });
    } else {
      this.loaderService.notifyStructuresChanged();
    }
    this.maybePersist();
  }

  private extractStructureSyncTaskPayload(task: Task): { payload: StructureSyncTaskPayload | null; source: string; rawData: unknown } {
    const directData = (task as any)?.data;
    if (directData && typeof directData === 'object' && !Array.isArray(directData)) {
      return { payload: directData as StructureSyncTaskPayload, source: 'task.data', rawData: directData };
    }

    const statusData = (task.status?.data as any)?.data;
    if (typeof statusData === 'string') {
      return { payload: JSON.parse(statusData) as StructureSyncTaskPayload, source: 'status.data.data:string', rawData: statusData };
    }
    if (statusData && typeof statusData === 'object' && !Array.isArray(statusData)) {
      return { payload: statusData as StructureSyncTaskPayload, source: 'status.data.data:object', rawData: statusData };
    }

    return { payload: null, source: 'none', rawData: statusData ?? directData };
  }

  private markRecentlyApplied(uid: string | undefined): void {
    if (!uid) return;
    this.recentlyAppliedUids.set(uid, Date.now());
  }

  private wasRecentlyApplied(uid: string | undefined): boolean {
    if (!uid) return false;
    const ts = this.recentlyAppliedUids.get(uid);
    if (ts === undefined) return false;
    if (Date.now() - ts > MmStructureSyncService.LOCAL_APPLY_TTL_MS) {
      this.recentlyAppliedUids.delete(uid);
      return false;
    }
    return true;
  }

  private serializeError(e: unknown): Record<string, unknown> {
    const err = e as any;
    return {
      message: err?.message,
      status: err?.status,
      statusText: err?.statusText,
      url: err?.url,
      errorBody: err?.error,
    };
  }

  private toPositiveNumber(value: unknown): number | null {
    const parsed = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private getStructureSyncTaskFailureMessage(task: Task): string | null {
    const statusLog = (task as any)?.statusLog;
    if (!Array.isArray(statusLog)) return null;

    for (let i = statusLog.length - 1; i >= 0; i--) {
      const text = statusLog[i]?.data?.data;
      if (typeof text === 'string' && text.trim()) return text.trim();
    }
    return null;
  }

  private notifyStructureSyncResolutionFailure(message: string, info: Record<string, unknown>): void {
    console.error('[MM-SYNC-RESOLVE-FAIL]', message, info);
    this.errorsSubject.next({ kind: StructureSyncErrorKind.ResolutionFailed, reason: message, info });
  }

  private belongsToCurrentProject(payloadProjectId?: string): boolean {
    const currentProjectMyDocmaUid = this.loaderService.projectSubject.value?.data?.MyDocmaUid;
    if (!payloadProjectId || !currentProjectMyDocmaUid) return true;
    return payloadProjectId === currentProjectMyDocmaUid;
  }

  private applyPayload(payload: StructureSyncPayload): void {
    const data = this.loaderService.getCurrentStructures();
    if (!data?.length) return;
    const treeMounted = this.patchSubject.observed;

    const shellUid = payload.mydocmaUid;
    if (shellUid && this.pendingShells.has(shellUid) && payload.op !== 'create') {
      const shell = this.pendingShells.get(shellUid)!;
      if (payload.op === 'update') {
        if (payload.name !== undefined) shell.name = payload.name;
        if (payload.desc !== undefined) shell.desc = payload.desc;
        this.markRecentlyApplied(shellUid);
      } else if (payload.op === 'reorder') {
        shell.parentUid = payload.parentId ?? shell.parentUid;
        shell.sortId = payload.newSortId;
        this.markRecentlyApplied(shellUid);
      } else if (payload.op === 'delete') {
        this.pendingShells.delete(shellUid);
      }
      return;
    }

    if (payload.op === 'update') {
      const target = this.coreUpdate(data, payload.mydocmaUid, payload.name, payload.desc);
      if (!target) return;
      this.patchSubject.next({ op: 'update', node: target });
      this.maybePersist();
    } else if (payload.op === 'delete') {
      const target = this.coreDelete(data, payload.mydocmaUid);
      if (!target) return;
      this.patchSubject.next({ op: 'delete', node: target });
      this.maybePersist();
      this.resetSelectedIfDeleted(new Set([payload.mydocmaUid!]));
    } else if (payload.op === 'reorder') {
      const result = this.coreReorder(data, payload.mydocmaUid, payload.parentId, payload.newSortId);
      if (!result) return;
      this.patchSubject.next({ op: 'reorder', node: result.node, parent: result.parent });
      this.maybePersist();
    } else if (payload.op === 'create') {
      if (!payload.mydocmaUid || !payload.parentId) return;
      this.pendingShells.set(payload.mydocmaUid, {
        uid: payload.mydocmaUid,
        parentUid: payload.parentId,
        name: payload.name,
        desc: payload.desc,
        level: payload.level,
      });
      return;
    } else {
      return;
    }

    this.markRecentlyApplied(payload.mydocmaUid);

    if (!treeMounted) {
      this.loaderService.notifyStructuresChanged();
    }
  }

  public applySyncDiff(newRoots: Structure[]): void {
    const currentRoots = this.loaderService.getCurrentStructures();
    if (!currentRoots?.length || !newRoots?.length) return;

    const oldFlat = this.flattenForDiff(currentRoots);
    const newFlat = this.flattenForDiff(newRoots);

    const deleteUids: string[] = [];
    const updateOps: Array<{ uid: string; name1: string; name2: string }> = [];
    const createOps: Array<{ node: Structure; parentUid: string | null; level: number; sortId: number }> = [];
    const reorderOps: Array<{ uid: string; newParentUid: string | null; newSortId: number }> = [];

    for (const [uid, _] of oldFlat) {
      if (!newFlat.has(uid)) deleteUids.push(uid);
    }
    for (const [uid, n] of newFlat) {
      const o = oldFlat.get(uid);
      if (!o) {
        createOps.push({ node: n.node, parentUid: n.parentUid, level: n.level, sortId: n.sortId });
        continue;
      }
      if (o.node.Name1 !== n.node.Name1 || o.node.Name2 !== n.node.Name2) {
        updateOps.push({ uid, name1: n.node.Name1 ?? '', name2: n.node.Name2 ?? '' });
      }
      if (o.parentUid !== n.parentUid || o.sortId !== n.sortId) {
        reorderOps.push({ uid, newParentUid: n.parentUid, newSortId: n.sortId });
      }
    }

    if (!deleteUids.length && !updateOps.length && !createOps.length && !reorderOps.length) return;

    const treeMounted = this.patchSubject.observed;

    for (const u of updateOps) {
      const target = this.coreUpdate(currentRoots, u.uid, u.name1, u.name2);
      if (!target) continue;
      if (treeMounted) this.patchSubject.next({ op: 'update', node: target });
    }

    for (const uid of deleteUids) {
      const target = this.coreDelete(currentRoots, uid);
      if (!target) continue;
      if (treeMounted) this.patchSubject.next({ op: 'delete', node: target });
    }

    const createsByLevel = [...createOps].sort((a, b) => a.level - b.level);
    for (const c of createsByLevel) {
      const result = this.coreInsertNode(currentRoots, c.node, c.parentUid, c.sortId);
      if (!result) continue;
      const insertedUid = result.node.MyDocmaUid;
      if (insertedUid) this.pendingShells.delete(insertedUid);
      if (treeMounted) this.patchSubject.next({ op: 'create', node: result.node, parent: result.parent });
    }

    const touchedParents = new Map<string, Structure>();
    for (const r of reorderOps) {
      const result = this.coreReorder(currentRoots, r.uid, r.newParentUid, r.newSortId);
      if (!result) continue;
      const newParentKey = result.parent.Id?.toString();
      if (newParentKey) touchedParents.set(newParentKey, result.parent);
      if (result.previousParent !== result.parent) {
        const oldParentKey = result.previousParent.Id?.toString();
        if (oldParentKey) touchedParents.set(oldParentKey, result.previousParent);
      }
    }
    if (treeMounted) {
      for (const parent of touchedParents.values()) {
        const representative = parent.Children?.[0] ?? parent;
        this.patchSubject.next({ op: 'reorder', node: representative, parent });
      }
    }

    if (deleteUids.length) this.resetSelectedIfDeleted(new Set(deleteUids));
    this.reindexAllStructureSortIds(currentRoots);
    if (!treeMounted) this.loaderService.notifyStructuresChanged();
    this.maybePersist();
  }

  private reindexAllStructureSortIds(roots: Structure[]): void {
    const stack: Structure[] = [...roots];
    while (stack.length) {
      const node = stack.pop()!;
      const children = node?.Children;
      if (!children?.length) continue;
      for (let i = 0; i < children.length; i++) {
        children[i].StructureSortId = i;
        stack.push(children[i]);
      }
    }
  }

  private coreUpdate(data: Structure[], uid: string | undefined, name?: string, desc?: string): Structure | null {
    const target = this.findByMyDocmaUid(data, uid);
    if (!target) return null;
    if (name !== undefined) target.Name1 = name;
    if (desc !== undefined) target.Name2 = desc;
    return target;
  }

  private coreDelete(data: Structure[], uid: string | undefined): Structure | null {
    if (!uid) return null;
    const target = this.findByMyDocmaUid(data, uid);
    if (!target) return null;
    this.removeByMyDocmaUid(data, uid);
    return target;
  }

  private coreInsertNode(data: Structure[], node: Structure, parentUid: string | null | undefined, sortId?: number): { node: Structure; parent: Structure } | null {
    if (!node?.MyDocmaUid) return null;
    const parent = (parentUid ? this.findByMyDocmaUid(data, parentUid) : null) ?? data[0];
    if (!parent) return null;
    if (!parent.Children) parent.Children = [];
    if (parent.Children.some(c => c.MyDocmaUid === node.MyDocmaUid)) return null;
    if (sortId !== undefined && sortId >= 0 && sortId < parent.Children.length) {
      parent.Children.splice(sortId, 0, node);
    } else {
      parent.Children.push(node);
    }
    parent.Children.forEach((s, i) => s.StructureSortId = i);
    return { node, parent };
  }

  private coreReorder(
    data: Structure[],
    uid: string | undefined,
    newParentUid: string | null | undefined,
    newSortId: number | undefined,
  ): { node: Structure; parent: Structure; previousParent: Structure } | null {
    if (!uid || newSortId === undefined) return null;
    const node = this.findByMyDocmaUid(data, uid);
    if (!node) return null;
    const currentParent = this.findParentOfMyDocmaUid(data, uid);
    const currentSiblings = currentParent?.Children;
    if (!currentParent || !currentSiblings) return null;
    const fromIdx = currentSiblings.findIndex(c => c.MyDocmaUid === uid);
    if (fromIdx < 0) return null;

    const newParent = (newParentUid ? this.findByMyDocmaUid(data, newParentUid) : null) ?? currentParent ?? data[0] ?? null;
    if (!newParent) return null;

    currentSiblings.splice(fromIdx, 1);
    if (!newParent.Children) newParent.Children = [];
    const newSiblings = newParent.Children;
    const insertIdx = Math.max(0, Math.min(newSortId, newSiblings.length));
    newSiblings.splice(insertIdx, 0, node);
    newSiblings.forEach((s, i) => s.StructureSortId = i);
    if (currentParent !== newParent) {
      currentSiblings.forEach((s, i) => s.StructureSortId = i);
    }

    return { node, parent: newParent, previousParent: currentParent };
  }

  private resetSelectedIfDeleted(deletedUids: Set<string>): void {
    const state = this.filterService.filterState;
    const selectedUid = state?.structure?.MyDocmaUid;
    if (!selectedUid || !deletedUids.has(selectedUid)) return;
    const root = state?.project?.Structure;
    if (!root) return;
    this.filterService.setStructure(root);
  }

  private maybePersist(): void {
    this.loaderService.persistCurrentStructures();
  }

  private findByMyDocmaUid(nodes: Structure[], mydocmaUid?: string): Structure | null {
    if (!mydocmaUid) return null;
    for (const n of nodes) {
      if (n?.MyDocmaUid === mydocmaUid) return n;
      const children = n?.Children;
      const found = children?.length ? this.findByMyDocmaUid(children, mydocmaUid) : null;
      if (found) return found;
    }
    return null;
  }

  private removeByMyDocmaUid(nodes: Structure[], mydocmaUid?: string): boolean {
    if (!mydocmaUid) return false;
    for (let i = 0; i < nodes.length; i++) {
      if (nodes[i]?.MyDocmaUid === mydocmaUid) {
        nodes.splice(i, 1);
        return true;
      }
      const children = nodes[i]?.Children;
      if (children?.length && this.removeByMyDocmaUid(children, mydocmaUid)) {
        return true;
      }
    }
    return false;
  }

  private findParentOfMyDocmaUid(roots: Structure[], uid: string): Structure | null {
    const stack: Structure[] = [...roots];
    while (stack.length) {
      const cur = stack.pop()!;
      const children = cur?.Children;
      if (children?.length) {
        if (children.some(c => c.MyDocmaUid === uid)) return cur;
        for (const c of children) stack.push(c);
      }
    }
    return null;
  }

  private flattenForDiff(roots: Structure[]): Map<string, { node: Structure; parentUid: string | null; sortId: number; level: number }> {
    const map = new Map<string, { node: Structure; parentUid: string | null; sortId: number; level: number }>();
    const stack: Array<{ node: Structure; parentUid: string | null; indexInParent: number }> = [];
    for (let i = 0; i < roots.length; i++) {
      stack.push({ node: roots[i], parentUid: null, indexInParent: i });
    }
    while (stack.length) {
      const { node, parentUid, indexInParent } = stack.pop()!;
      if (node?.MyDocmaUid) {
        map.set(node.MyDocmaUid, {
          node,
          parentUid,
          sortId: indexInParent,
          level: node.Level ?? 0,
        });
      }
      const children = node?.Children;
      if (children?.length) {
        const childParentUid = node.MyDocmaUid ?? parentUid;
        for (let i = 0; i < children.length; i++) {
          stack.push({ node: children[i], parentUid: childParentUid, indexInParent: i });
        }
      }
    }
    return map;
  }
}
